/*
 * Copyright (c) 2026-present Amida Technology Solutions, Inc.
 * All rights reserved.
 *
 * Licensed under the Amida proprietary license.
 * See LICENSE file in the project root for license information.
 */

import { useQuery } from '@tanstack/react-query';
import type { QueryClient } from '@tanstack/react-query';
import {
  Outlet,
  createRootRouteWithContext,
  useRouterState,
} from '@tanstack/react-router';

import { meQuery, permissionsQuery } from '@/api/auth';
import { Appearance } from '@/components/Appearance';
import { Rail } from '@/components/Rail';
import { SessionGuard } from '@/components/SessionGuard';
import { FloatingLayer } from '@/components/ui/FloatingLayer';
import { Icon } from '@/components/ui/Icon';
import { TopBar } from '@/components/TopBar';
import { Toaster } from '@/components/ui/Toaster';

/**
 * The application frame.
 *
 * The sign-in screen renders outside it: it has no rail, no top bar, and no
 * business data of any kind, because it renders before anyone has
 * authenticated (SCR-8).
 */
function Root() {
  const path = useRouterState({ select: (s) => s.location.pathname });
  const onSignIn = path === '/signin';

  const { data: me } = useQuery({ ...meQuery(), enabled: !onSignIn });
  const { data: permissions } = useQuery({
    ...permissionsQuery(),
    enabled: !onSignIn,
  });

  if (onSignIn) {
    return <Outlet />;
  }

  // Somebody in no mapped group is signed in and powerless. Say so once, here,
  // rather than letting every screen show its own refusal -- and name who can
  // fix it. Silently granting a default role would be the worse failure.
  const hasNoRole = me !== undefined && me.roles.length === 0;

  return (
    <>
      <Appearance />
      {/* Only once we know who this is, and only inside the frame: the
          sign-in screen has no session to guard. */}
      {me ? (
        <SessionGuard
          idleMinutes={me.session_idle_timeout_minutes}
          warningSeconds={me.session_warning_seconds}
        />
      ) : null}
      <Rail permissions={permissions} me={me} />
      <div className="app">
        <TopBar />
        <main id="content" tabIndex={-1}>
          <div className="wrap">{hasNoRole ? <NoRole /> : <Outlet />}</div>
          {/* Menus and filter lists open here, inside the landmark, rather
              than off the end of the body (ADW-5553). */}
          <FloatingLayer />
        </main>
      </div>
      <Toaster />
    </>
  );
}

function NoRole() {
  return (
    <section className="card">
      <div className="empty">
        <span className="empty-i">
          <Icon name="shield" size={22} />
        </span>
        <b>You do not have a role yet</b>
        <p>
          You are signed in, but your account is in no group that Vellum recognises, so
          there is nothing here to show you. An administrator grants a role by mapping
          your identity-provider group on the Groups screen. It takes effect the next time
          you sign in.
        </p>
      </div>
    </section>
  );
}

/** What every route can reach. The query client, so a route can prefetch. */
export interface RouterContext {
  queryClient: QueryClient;
}

export const rootRoute = createRootRouteWithContext<RouterContext>()({
  component: Root,
});
